import { FiHighlightService } from '@fi-shared/fi-highlight/fi-highlight.service';
import { ChangeDetectorRef, Directive, Inject, Injector, Input, Renderer2 } from '@angular/core';
import { NewsFeedModel } from '../news-feed.model';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { NewsFeedModuleService } from '../news-feed-module.service';
import { FiImageSize } from '@fi-shared/types/image-size';
import { isDefined } from '@fi-shared/utils/is-defined';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateNames } from '@fi-app/shared/fi-state-proxy/fi-state-names';
import { FileType } from '@fi-shared/types';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { truncateAtWord } from '@fi-shared/utils/truncate-at-word';
import { INewsFeedContent, INewsFeedContentLabel } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { AppTextService } from '@fi-shared/app-text';

@Directive()
export class NewsFeedItemContentBase {
    @Input() newsItem: NewsFeedModel;
    @Input() newsItemMediaBankReferences: IMediaBankFileReference[] = [];

    FiStateNames = FiStateNames;
    FileType = FileType;
    FiImageSize = FiImageSize;

    constructor(
        @Inject(NewsFeedModuleService) private newsFeedModuleService: NewsFeedModuleService,
        @Inject(MediaBankHelperService) private mediaBankHelper: MediaBankHelperService,
        @Inject(MediaViewerService) private mediaViewerService: MediaViewerService,
        protected mediaStorageService: FiMediaStorageService,
        protected highlightService: FiHighlightService,
        private contentLanguageSharedService: FiContentLanguageSharedService,
        protected renderer: Renderer2,
        protected injector: Injector,
        private userDataService: FiUserDataService,
        protected fiFormatDateService: FiFormatDateService,
        protected appTextService: AppTextService,
        protected customerDataService: FiCustomerDataService,
        protected cdr: ChangeDetectorRef,
    ) {
        this.newsItem = <any>{};
    }

    getTrimmedValue(value): string {
        return this.newsFeedModuleService.GetTrimmedValue(value);
    }

    getPreferredContentLabel(labels: INewsFeedContentLabel[]): INewsFeedContentLabel {
        if (!isDefined(labels) || !Array.isArray(labels)) return undefined;

        let defaultLabel,
            defaultLanguage = this.contentLanguageSharedService.getDefaultContentLanguageId();

        for (let i = 0; i < labels.length; i++) {
            if (
                labels[i].Name &&
                labels[i].ContentLanguageId === this.userDataService.userData.PreferredContentLanguage
            )
                return labels[i];

            if (labels[i].ContentLanguageId === defaultLanguage) defaultLabel = labels[i];
        }
        return defaultLabel;
    }

    getPreferredContentLabelName(labels: INewsFeedContentLabel[]): string {
        const preferredContentLabel = this.getPreferredContentLabel(labels);
        return preferredContentLabel?.Name ?? '';
    }

    isContentVisible(content): boolean {
        return content.ContentLanguageId === this.userDataService.userData.PreferredContentLanguage;
    }

    getNameValue(names: any[], field?: string): string {
        return this.newsFeedModuleService.GetNameValue(names, field);
    }

    getContentValue(): string {
        const nameValue = this.getNameValue(this.newsItem.Content.NewsFeedDocumentPublishedContents, 'Content');
        return nameValue;
    }

    getPreviewFileUrl(PreviewFileName): string {
        return this.mediaStorageService.getMediaFileUrlByFileName(
            PreviewFileName + FiAppSettings.DefaultMediaBankImageExtension,
            FiImageSize.Thumbnail /*'DocumentPreview'*/,
            this.newsItem.ChangedOn.toISOString(),
        );
    }

    getContentUrl(model: INewsFeedContent, size?: FiImageSize): string {
        return this.mediaStorageService.getMediaFileUrl(model, { size: size });
    }

    previewNewsItemContent($event: MouseEvent) {
        this.preview(
            $event,
            this.mediaBankHelper.getMediaBankFileReferenceFromNewsItemContent(
                this.newsItem.Content,
                this.newsItem.ObjectId,
                this.getNameValue(this.newsItem.Content.Names),
            ),
        );
    }

    previewNewsItemMediaBankReference($event: MouseEvent, mediaBankReference: IMediaBankFileReference) {
        let previewMediaBankReferences: IMediaBankFileReference[] = [];
        if (this.isImageOrVimeoVideo(mediaBankReference)) {
            previewMediaBankReferences = this.newsItemMediaBankReferences
                .filter((mediaBankReference) => this.isImageOrVimeoVideo(mediaBankReference))
                .map((mediaBankReference) => {
                    return this.mediaBankHelper.getMediaBankFileReferenceFromNewsItemContent(
                        mediaBankReference,
                        mediaBankReference.MediaBankFileId,
                        mediaBankReference.Name,
                    );
                });
        }
        this.preview($event, mediaBankReference, previewMediaBankReferences);
    }

    private isImageOrVimeoVideo(mediaBankReference: IMediaBankFileReference): boolean {
        return (
            this.mediaBankHelper.isImageFileType(mediaBankReference.FileType) ||
            this.mediaBankHelper.isVimeoVideoFile(mediaBankReference.FileType, mediaBankReference.StorageType)
        );
    }

    getNewsItemContentName(content: INewsFeedContent): string {
        return content.Names ? this.getNameValue(content.Names) : content.Name;
    }

    preview(
        $event: MouseEvent,
        mediaBankReference: IMediaBankFileReference,
        mediaBankReferences: IMediaBankFileReference[] = [],
    ) {
        if ($event) {
            $event.stopPropagation();
            $event.preventDefault();
        }

        if (mediaBankReference.FileType === FileType.Pdf) {
            this.mediaBankHelper.downloadMediaBankFileRef(mediaBankReference, { openPdfInNewWindow: true });
            return;
        }

        this.mediaViewerService.Show(
            this.mediaStorageService.getMediaFileUrl(mediaBankReference, { size: FiImageSize.Large }),
            undefined,
            mediaBankReferences.length ? mediaBankReferences : [mediaBankReference],
        );
    }

    getMediaBankFileIcon(fileType: FileType): string {
        return this.mediaBankHelper.getMediaBankFileIcon(fileType);
    }

    mediaBankFileContent(): string {
        return truncateAtWord(
            this.fullMediaBankFileContent(),
            this.customerDataService.customerData.NewsFeedSummaryLength,
        );
    }

    showMediaBankFileReadMore(): boolean {
        return this.fullMediaBankFileContent().length > this.customerDataService.customerData.NewsFeedSummaryLength;
    }

    private fullMediaBankFileContent(): string {
        return this.getNameValue(this.newsItem.Content.Descriptions) || this.newsItem.Content.Summary || '';
    }
}
